import React, { useState } from 'react';
import styled from 'styled-components';
import { HelpCircle, Search, ChevronDown, ChevronUp, Filter, Star, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const SearchSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 30px;
  margin-bottom: 30px;
  text-align: center;
`;

const SearchTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 15px 0;
`;

const SearchSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 25px 0;
`;

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 15px 50px 15px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 25px;
  font-size: 16px;
  background: #f9fafb;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
  
  &:hover {
    background: #2563eb;
  }
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const CategoryTab = styled.button`
  padding: 10px 20px;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border: 1px solid ${props => props.active ? '#3b82f6' : '#d1d5db'};
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }
`;

const PopularFAQs = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
`;

const PopularTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PopularList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 10px;
`;

const PopularItem = styled.div`
  background: rgba(255, 255, 255, 0.7);
  padding: 12px 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

const PopularQuestion = styled.p`
  font-size: 13px;
  color: #92400e;
  margin: 0;
  font-weight: 500;
`;

const FAQGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FAQCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  }
`;

const FAQHeader = styled.div`
  padding: 20px 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.expanded ? '#f8fafc' : 'white'};
  border-bottom: ${props => props.expanded ? '1px solid #e5e7eb' : 'none'};
  
  &:hover {
    background: #f8fafc;
  }
`;

const FAQQuestion = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FAQCategoryBadge = styled.span`
  background: ${props => {
    switch(props.category) {
      case '배송': return '#dbeafe';
      case '반품교환': return '#fecaca';
      case '결제': return '#d1fae5';
      case '회원': return '#fde68a';
      case '쿠폰': return '#e9d5ff';
      case '적립금': return '#fed7d7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.category) {
      case '배송': return '#1d4ed8';
      case '반품교환': return '#dc2626';
      case '결제': return '#047857';
      case '회원': return '#d97706';
      case '쿠폰': return '#7c3aed';
      case '적립금': return '#e11d48';
      default: return '#6b7280';
    }
  }};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
`;

const FAQExpandIcon = styled.div`
  color: #9ca3af;
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const FAQContent = styled.div`
  padding: ${props => props.expanded ? '0 25px 25px 25px' : '0'};
  max-height: ${props => props.expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`;

const FAQAnswer = styled.div`
  color: #4b5563;
  line-height: 1.7;
  font-size: 15px;
  padding-top: 15px;
  white-space: pre-line;
`;

const FAQMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #f3f4f6;
`;

const FAQRating = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RatingButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: ${props => props.active ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  border: none;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#e5e7eb'};
  }
`;

const FAQActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

function SupportFAQ() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [faqRatings, setFaqRatings] = useState({});

  const categories = [
    { id: 'all', name: '전체' },
    { id: '배송', name: '배송' },
    { id: '반품교환', name: '반품교환' },
    { id: '결제', name: '결제' },
    { id: '회원', name: '회원' },
    { id: '쿠폰', name: '쿠폰' },
    { id: '적립금', name: '적립금' }
  ];

  const faqData = [
    {
      id: 1,
      question: '주문한 상품은 언제 배송되나요?',
      answer: '주문 완료 후 평일 기준 1-3일 내에 배송됩니다. 배송 지역과 상품에 따라 다소 차이가 있을 수 있으며, 제주도나 도서지역은 추가 배송일이 소요될 수 있습니다. 배송 현황은 마이페이지의 주문내역에서 실시간으로 확인하실 수 있습니다.',
      category: '배송',
      helpful: 245,
      notHelpful: 12,
      views: 1847,
      lastUpdated: '2024-02-14'
    },
    {
      id: 2,
      question: '반품/교환은 어떻게 하나요?',
      answer: '상품 수령 후 7일 이내에 반품/교환 신청이 가능합니다. 마이페이지 > 주문내역에서 해당 주문을 선택하여 반품/교환 신청을 하실 수 있습니다. 단, 개봉하여 사용한 상품이나 고객 변심으로 인한 경우 배송비가 발생할 수 있습니다.',
      category: '반품교환',
      helpful: 189,
      notHelpful: 8,
      views: 1523,
      lastUpdated: '2024-02-10'
    },
    {
      id: 3,
      question: '쿠폰은 어떻게 사용하나요?',
      answer: '쿠폰은 결제 단계에서 사용하실 수 있습니다. 장바구니에서 주문서 작성 시 "쿠폰 사용" 버튼을 클릭하여 보유한 쿠폰 중 사용 가능한 쿠폰을 선택해주세요. 쿠폰마다 최소 주문금액과 사용기한이 다르니 확인 후 사용해주세요.',
      category: '쿠폰',
      helpful: 167,
      notHelpful: 5,
      views: 1234,
      lastUpdated: '2024-02-08'
    },
    {
      id: 4,
      question: '적립금은 언제 지급되나요?',
      answer: '적립금은 상품 구매 후 배송 완료 확인 시점에서 7일 후에 자동으로 지급됩니다. 적립률은 상품과 회원등급에 따라 차이가 있으며, 적립된 포인트는 마이페이지에서 확인하실 수 있습니다. 적립금은 다음 주문시 현금처럼 사용 가능합니다.',
      category: '적립금',
      helpful: 134,
      notHelpful: 3,
      views: 987,
      lastUpdated: '2024-02-05'
    },
    {
      id: 5,
      question: '회원가입 시 인증이 안 돼요',
      answer: '휴대폰 인증이 되지 않는 경우 다음을 확인해주세요: 1) 입력한 휴대폰 번호가 정확한지 확인 2) SMS 수신 차단 설정이 되어있는지 확인 3) 해외 휴대폰의 경우 인증이 제한될 수 있습니다. 그래도 해결되지 않으면 고객센터로 문의해주세요.',
      category: '회원',
      helpful: 98,
      notHelpful: 7,
      views: 756,
      lastUpdated: '2024-02-12'
    },
    {
      id: 6,
      question: '결제가 실패했는데 어떻게 하나요?',
      answer: '결제 실패의 주요 원인은 다음과 같습니다: 1) 카드 한도 초과 2) 비밀번호 오입력 3) 보안카드/OTP 오입력 4) 해외결제 차단 설정. 카드사에 문의하여 결제 가능 여부를 확인하시거나, 다른 결제수단을 이용해보세요.',
      category: '결제',
      helpful: 156,
      notHelpful: 11,
      views: 1345,
      lastUpdated: '2024-02-11'
    }
  ];

  const popularFAQs = faqData
    .sort((a, b) => b.views - a.views)
    .slice(0, 4);

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchTerm === '' ||
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const sortedFAQs = [...filteredFAQs].sort((a, b) => {
    switch(sortBy) {
      case 'popular': return b.views - a.views;
      case 'helpful': return b.helpful - a.helpful;
      case 'recent': return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      case 'alphabetical': return a.question.localeCompare(b.question);
      default: return 0;
    }
  });

  const handleSearch = () => {
    console.log('Searching for:', searchTerm);
  };

  const toggleFAQ = (faqId) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  const handleRating = (faqId, isHelpful) => {
    setFaqRatings(prev => ({
      ...prev,
      [faqId]: isHelpful ? 'helpful' : 'notHelpful'
    }));
    console.log(`FAQ ${faqId} rated as ${isHelpful ? 'helpful' : 'not helpful'}`);
  };

  const handlePopularClick = (faq) => {
    setExpandedFAQ(faq.id);
    setTimeout(() => {
      document.getElementById(`faq-${faq.id}`)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <ContentLayout 
      title="자주 묻는 질문"
      subtitle="고객님들이 자주 문의하시는 내용을 정리했습니다"
    >
      <Container>
        <SearchSection>
          <SearchTitle>어떤 것이 궁금하신가요?</SearchTitle>
          <SearchSubtitle>키워드를 입력하시면 관련된 질문을 찾아드립니다</SearchSubtitle>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="예: 배송, 반품, 결제 등"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <SearchButton onClick={handleSearch}>
              <Search size={18} />
            </SearchButton>
          </SearchContainer>
        </SearchSection>

        <PopularFAQs>
          <PopularTitle>
            <Star size={18} />
            인기 질문
          </PopularTitle>
          <PopularList>
            {popularFAQs.map(faq => (
              <PopularItem key={faq.id} onClick={() => handlePopularClick(faq)}>
                <PopularQuestion>{faq.question}</PopularQuestion>
              </PopularItem>
            ))}
          </PopularList>
        </PopularFAQs>

        <CategoryTabs>
          {categories.map(category => (
            <CategoryTab
              key={category.id}
              active={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </CategoryTab>
          ))}
        </CategoryTabs>

        <FilterSection>
          <FilterSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="popular">인기순</option>
            <option value="helpful">도움이 된 순</option>
            <option value="recent">최신순</option>
            <option value="alphabetical">가나다순</option>
          </FilterSelect>
          
          <span style={{fontSize: '12px', color: '#6b7280'}}>
            {sortedFAQs.length}개의 질문
          </span>
        </FilterSection>

        {sortedFAQs.length > 0 ? (
          <FAQGrid>
            {sortedFAQs.map(faq => (
              <FAQCard key={faq.id} id={`faq-${faq.id}`}>
                <FAQHeader 
                  expanded={expandedFAQ === faq.id}
                  onClick={() => toggleFAQ(faq.id)}
                >
                  <FAQQuestion>
                    <FAQCategoryBadge category={faq.category}>
                      {faq.category}
                    </FAQCategoryBadge>
                    {faq.question}
                  </FAQQuestion>
                  <FAQExpandIcon expanded={expandedFAQ === faq.id}>
                    <ChevronDown size={20} />
                  </FAQExpandIcon>
                </FAQHeader>
                
                <FAQContent expanded={expandedFAQ === faq.id}>
                  <FAQAnswer>{faq.answer}</FAQAnswer>
                  
                  <FAQMeta>
                    <FAQRating>
                      <span style={{fontSize: '12px', color: '#6b7280'}}>
                        이 답변이 도움이 되었나요?
                      </span>
                      <RatingButton
                        active={faqRatings[faq.id] === 'helpful'}
                        onClick={() => handleRating(faq.id, true)}
                      >
                        <ThumbsUp size={12} />
                        도움됨 ({faq.helpful})
                      </RatingButton>
                      <RatingButton
                        active={faqRatings[faq.id] === 'notHelpful'}
                        onClick={() => handleRating(faq.id, false)}
                      >
                        <ThumbsDown size={12} />
                        아니요 ({faq.notHelpful})
                      </RatingButton>
                    </FAQRating>
                    
                    <FAQActions>
                      <ActionButton>
                        <MessageSquare size={12} />
                        1:1 문의
                      </ActionButton>
                    </FAQActions>
                  </FAQMeta>
                </FAQContent>
              </FAQCard>
            ))}
          </FAQGrid>
        ) : (
          <EmptyState>
            <EmptyIcon>
              <HelpCircle size={40} />
            </EmptyIcon>
            <h3 style={{margin: '0 0 10px 0', color: '#374151'}}>
              검색 결과가 없습니다
            </h3>
            <p style={{margin: 0, color: '#6b7280'}}>
              다른 검색어를 사용하거나 카테고리를 변경해보세요
            </p>
          </EmptyState>
        )}
      </Container>
    </ContentLayout>
  );
}

export default SupportFAQ;